export interface ScreenStop {
  id: string;
  x: number;
  y: number;
}

export interface PlacedStop extends ScreenStop {
  displaced: boolean;
}

export interface MapViewport {
  width: number;
  height: number;
  left?: number;
  right?: number;
  top?: number;
  bottom?: number;
}

export function spreadOverlappingStops(
  stops: ScreenStop[],
  viewport: MapViewport,
  markerSize: number
): PlacedStop[] {
  const gapX = markerSize + 42;
  const gapY = markerSize + 32;
  const minX = (viewport.left ?? 0) + markerSize / 2 + 8;
  const maxX = viewport.width - (viewport.right ?? 0) - markerSize / 2 - 30;
  const minY = (viewport.top ?? 0) + markerSize + 25;
  const maxY = viewport.height - (viewport.bottom ?? 0) - 4;
  const remaining = new Set(
    stops.filter(
      (stop) =>
        stop.x >= -markerSize &&
        stop.x <= viewport.width + markerSize &&
        stop.y >= -markerSize &&
        stop.y <= viewport.height + markerSize
    )
  );
  const groups: ScreenStop[][] = [];

  for (const stop of stops) {
    if (!remaining.delete(stop)) continue;
    const group = [stop];
    for (let i = 0; i < group.length; i++) {
      for (const other of remaining) {
        if (
          Math.abs(group[i].x - other.x) < markerSize + 8 &&
          Math.abs(group[i].y - other.y) < markerSize + 8
        ) {
          group.push(other);
          remaining.delete(other);
        }
      }
    }
    groups.push(group);
  }

  const placed = new Map<string, PlacedStop>();
  const occupied: Array<{
    left: number;
    right: number;
    top: number;
    bottom: number;
  }> = [];
  const boundsOf = (x: number, y: number) => ({
    left: x - markerSize / 2 - 8,
    right: x + markerSize / 2 + 30,
    top: y - markerSize - 25,
    bottom: y + 4,
  });
  const overlaps = (
    a: (typeof occupied)[number],
    b: (typeof occupied)[number]
  ) =>
    a.left < b.right &&
    a.right > b.left &&
    a.top < b.bottom &&
    a.bottom > b.top;

  for (const group of groups) {
    if (group.length !== 1) continue;
    const stop = group[0];
    placed.set(stop.id, { ...stop, displaced: false });
    occupied.push(boundsOf(stop.x, stop.y));
  }

  groups.sort((a, b) => a[0].x - b[0].x || a[0].y - b[0].y);
  for (const group of groups) {
    if (group.length === 1) continue;
    group.sort((a, b) => a.id.localeCompare(b.id));
    const centerX = group.reduce((sum, item) => sum + item.x, 0) / group.length;
    const centerY = group.reduce((sum, item) => sum + item.y, 0) / group.length;
    const columns = Math.min(
      Math.ceil(Math.sqrt(group.length)),
      Math.max(1, Math.floor((maxX - minX) / gapX) + 1)
    );
    const rows = Math.ceil(group.length / columns);
    const left = centerX - ((columns - 1) * gapX) / 2;
    const right = centerX + ((columns - 1) * gapX) / 2;
    const top = centerY - ((rows - 1) * gapY) / 2;
    const bottom = centerY + ((rows - 1) * gapY) / 2;
    const shiftX = left < minX ? minX - left : right > maxX ? maxX - right : 0;
    const shiftY = top < minY ? minY - top : bottom > maxY ? maxY - bottom : 0;
    const shifts = [{ x: 0, y: 0 }];
    for (let row = -6; row <= 6; row++) {
      for (let column = -6; column <= 6; column++) {
        if (row || column) shifts.push({ x: column * gapX, y: row * gapY });
      }
    }
    shifts.sort((a, b) => Math.hypot(a.x, a.y) - Math.hypot(b.x, b.y));
    const candidate = shifts.find((shift) => {
      const rectangles = group.map((_, index) => {
        const x = left + (index % columns) * gapX + shiftX + shift.x;
        const y = top + Math.floor(index / columns) * gapY + shiftY + shift.y;
        if (x < minX || x > maxX || y < minY || y > maxY) return null;
        return boundsOf(x, y);
      });
      return rectangles.every(
        (rect, index) =>
          rect &&
          occupied.every((other) => !overlaps(rect, other)) &&
          rectangles
            .slice(0, index)
            .every((other) => other && !overlaps(rect, other))
      );
    }) ?? { x: 0, y: 0 };
    group.forEach((item, index) => {
      const result = {
        ...item,
        x: left + (index % columns) * gapX + shiftX + candidate.x,
        y: top + Math.floor(index / columns) * gapY + shiftY + candidate.y,
        displaced: true,
      };
      placed.set(item.id, result);
      occupied.push(boundsOf(result.x, result.y));
    });
  }

  return stops.map(
    (stop) => placed.get(stop.id) ?? { ...stop, displaced: false }
  );
}
